import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import FormikField, { findValue, FormikSubFieldProps, useRequiredPlaceholderSuffix } from './FormikField'
import LocalDatePicker, { LocalDatePickerProps } from '../DatePicker/LocalDatePicker'

/**
 * A date field: it reads and writes the ISO date in the form itself, so a form no longer repeats isoValue and
 * onSelectDate. The date picker composes its placeholder from a translation key, so it gets the key of the field plus
 * the required suffix rather than a finished text.
 */
export default function FormikDatePickerField<Values>({ placeholderKey, ...props }: FormikSubFieldProps<LocalDatePickerProps, Values>): ReactElement {
    const { values, setFieldValue } = useFormikContext<Values>()

    const placeholderSuffix = useRequiredPlaceholderSuffix(props)
    const currentValue = useMemo(() => findValue(values, props.fieldName as string), [values, props.fieldName])

    return (
        <FormikField<LocalDatePickerProps, Values>
            component={LocalDatePicker}
            {...props}
            componentProps={{
                // Null, not undefined: an undefined value turns the date picker uncontrolled and it would keep showing
                // the date the user just cleared (HAM-3564).
                isoValue: currentValue || null,
                onSelectDate: (date: string) => setFieldValue(props.fieldName as string, date),
                placeholderSuffix,
                ...(placeholderKey ? { placeholderKey } : {}),
                ...props.componentProps,
            }}
        />
    )
}
